import {getCountryCallingCode, getCountries} from 'libphonenumber-js'
import type {CountryCode} from 'libphonenumber-js'
import type {Country} from '../../../../forms/Form/components/ConsentExperience/types'
import {getLocale} from '../../../../../lib/utils/localization'

export const DEFAULT_COUNTRY: CountryCode = 'US'

// Cache one runtime locale instance so SSR and hydration render the same country labels.
let _regionDisplayNames: Intl.DisplayNames | null = null
function getRegionDisplayNames(): Intl.DisplayNames {
  if (!_regionDisplayNames) {
    _regionDisplayNames = new Intl.DisplayNames([getLocale()], {type: 'region'})
  }
  return _regionDisplayNames
}

export type CountryOption = {
  code: CountryCode
  name: string
  callingCode: string
}

export type PhoneSubmissionDetails = {
  formattedPhone: string
  detectedCountry?: CountryCode
  displayInput?: string
}

/**
 * Get country name from code using Intl.DisplayNames, with fallback to the code itself
 */
export function getCountryName(countryCode: CountryCode): string {
  return getRegionDisplayNames().of(countryCode) ?? countryCode
}

/**
 * Build list of country options from allowed countries or all supported countries
 */
export function buildCountryOptions(allowedCountries?: Country[]): CountryOption[] {
  const allCountries = getCountries()
  const supportedCodes = new Set(allCountries)

  if (allowedCountries && allowedCountries.length > 0) {
    return allowedCountries
      .filter(c => supportedCodes.has(c.alpha as CountryCode))
      .map(c => ({
        code: c.alpha as CountryCode,
        name: getCountryName(c.alpha as CountryCode),
        callingCode: getCountryCallingCode(c.alpha as CountryCode),
      }))
  }

  return allCountries.map(code => ({
    code,
    name: getCountryName(code),
    callingCode: getCountryCallingCode(code),
  }))
}

/**
 * Get short country display for trigger button: US +1
 */
export function getCountryDisplayShort(countryCode: CountryCode): string {
  const callingCode = getCountryCallingCode(countryCode)
  return `${countryCode} +${callingCode}`
}

export function countryMatchesSearch(country: CountryOption, searchQuery: string): boolean {
  if (!searchQuery.trim()) return true

  const normalizedQuery = searchQuery.toLowerCase().trim()

  return (
    country.name.toLowerCase().includes(normalizedQuery) ||
    country.code.toLowerCase().includes(normalizedQuery) ||
    `+${country.callingCode}`.includes(normalizedQuery) ||
    country.callingCode.includes(normalizedQuery)
  )
}

export function filterCountriesBySearch(countries: CountryOption[], searchQuery: string): CountryOption[] {
  return countries.filter(c => countryMatchesSearch(c, searchQuery))
}

/**
 * Derive the submitted phone value and any supported detected country.
 */
export function getPhoneSubmissionDetails(
  rawInput: string,
  countryCode: CountryCode,
  parsePhoneNumber: typeof import('libphonenumber-js').parsePhoneNumberFromString,
): PhoneSubmissionDetails {
  const trimmedInput = rawInput.trim()

  if (!trimmedInput) {
    return {formattedPhone: ''}
  }

  if (trimmedInput.startsWith('+')) {
    const parsedInternationalNumber = parsePhoneNumber(trimmedInput)

    if (parsedInternationalNumber?.isPossible()) {
      return {
        formattedPhone: parsedInternationalNumber.format('E.164'),
        detectedCountry: parsedInternationalNumber.country,
        displayInput: parsedInternationalNumber.formatNational(),
      }
    }
  }

  const digitsOnly = rawInput.replace(/\D/g, '')

  if (!digitsOnly) {
    // Preserve non-empty invalid input so the existing PHONE validation can surface an error.
    return {formattedPhone: trimmedInput}
  }

  const callingCode = getCountryCallingCode(countryCode)
  const fullNumber = `+${callingCode}${digitsOnly}`
  const parsed = parsePhoneNumber(digitsOnly, countryCode)

  return {
    formattedPhone: parsed?.isPossible() ? parsed.format('E.164') : fullNumber,
  }
}

/**
 * Format phone number to E.164 or best-effort international format.
 */
export function formatPhoneForSubmission(
  rawInput: string,
  countryCode: CountryCode,
  parsePhoneNumber: typeof import('libphonenumber-js').parsePhoneNumberFromString,
): string {
  return getPhoneSubmissionDetails(rawInput, countryCode, parsePhoneNumber).formattedPhone
}
